/**
 * COMMONS: SINTETIZADOR DE AUDIO WEB AUDIO API - SECUENCIA
 * Generador de síntesis de sonido procedural en tiempo real para la dinámica Secuencia (Simon Says).
 * Produce tonos armónicos puros para los 6 pads con envolvente de ganancia ADSR suave (evita clicks/pops),
 * fanfarria de victoria, sonido de error con modulación de frecuencia, y bips de conteo regresivo.
 * No depende de archivos .mp3 externos y gestiona de forma segura el ciclo de vida del AudioContext
 * para cumplir con las políticas de autoplay de navegadores móviles y de escritorio.
 * 
 * @module utilities/Commons/SequenceAudioSynth
 */

/**
 * Frecuencias armónicas base en Hertz para los 6 pads de Secuencia:
 * - Pad 0: 440.00 Hz (Nota A4 - La)
 * - Pad 1: 554.37 Hz (Nota C#5 - Do#)
 * - Pad 2: 659.25 Hz (Nota E5 - Mi)
 * - Pad 3: 783.99 Hz (Nota G5 - Sol)
 * - Pad 4: 880.00 Hz (Nota A5 - La)
 * - Pad 5: 987.77 Hz (Nota B5 - Si)
 * @constant {number[]}
 */
export const PAD_FREQUENCIES = [
    440.00, // Pad 0 (Verde)
    554.37, // Pad 1 (Rojo)
    659.25, // Pad 2 (Amarillo)
    783.99, // Pad 3 (Azul)
    880.00, // Pad 4 (Morado)
    987.77  // Pad 5 (Naranja)
];

/**
 * Instancia singleton de AudioContext.
 * @type {AudioContext|null}
 */
let audioCtx = null;

/**
 * Nodo maestro de ganancia para control global de volumen.
 * @type {GainNode|null}
 */
let masterGain = null;

/**
 * Bandera para registrar si ya se intentó desbloquear el audio context con un gesto de usuario.
 * @type {boolean}
 */
let isUnlocked = false;

/**
 * Comprueba si la API de Web Audio es soportada en el entorno actual.
 * 
 * @returns {boolean} True si AudioContext o webkitAudioContext está disponible.
 */
export const isAudioSupported = () => {
    return typeof window !== 'undefined' && Boolean(window.AudioContext || window.webkitAudioContext);
};

/**
 * Obtiene o inicializa la instancia singleton de AudioContext.
 * 
 * @returns {AudioContext|null} La instancia activa de AudioContext o null si no está soportado.
 */
export const getAudioContext = () => {
    if (!isAudioSupported()) return null;

    if (!audioCtx) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        audioCtx = new AudioContextClass();

        masterGain = audioCtx.createGain();
        masterGain.gain.setValueAtTime(0.7, audioCtx.currentTime);
        masterGain.connect(audioCtx.destination);
    }

    return audioCtx;
};

/**
 * Desbloquea y reanuda de manera segura el AudioContext ante el primer gesto de usuario
 * (click, touchstart, pointerdown, keydown) cumpliendo con las políticas de autoplay de navegadores modernos.
 * 
 * @returns {Promise<boolean>} True si el AudioContext se encuentra en estado 'running'.
 */
export const unlockAudioContext = async () => {
    try {
        const ctx = getAudioContext();
        if (!ctx) return false;

        if (ctx.state === 'suspended') {
            await ctx.resume();
        }

        if (ctx.state === 'running') {
            isUnlocked = true;
            return true;
        }
    } catch (e) {
        // Silencioso ante restricciones de interacción de navegadores
    }
    return false;
};

/** Alias de desbloqueo */
export const initAudioSynth = unlockAudioContext;

/**
 * Auto-registra escuchas globales en window para desbloquear el AudioContext automáticamente en el primer toque.
 */
if (typeof window !== 'undefined') {
    const handleFirstGesture = () => {
        unlockAudioContext().then(() => {
            if (isUnlocked) {
                window.removeEventListener('pointerdown', handleFirstGesture);
                window.removeEventListener('touchstart', handleFirstGesture);
                window.removeEventListener('click', handleFirstGesture);
                window.removeEventListener('keydown', handleFirstGesture);
            }
        }).catch(() => {});
    };

    window.addEventListener('pointerdown', handleFirstGesture, { passive: true });
    window.addEventListener('touchstart', handleFirstGesture, { passive: true });
    window.addEventListener('click', handleFirstGesture, { passive: true });
    window.addEventListener('keydown', handleFirstGesture, { passive: true });
}

/**
 * Reproduce un tono sintetizado puro con envolvente de ganancia ADSR suave (evita clicks y chasquidos).
 * Permite combinar osciladores sine/triangle para conseguir un timbre cálido y definido.
 * 
 * @param {number} frequency - Frecuencia en Hertz del tono (ej. 440 para La4).
 * @param {number} [durationMs=300] - Duración total de la nota en milisegundos.
 * @param {('sine'|'triangle'|'sawtooth'|'square')} [waveform='sine'] - Tipo de onda del oscilador.
 * @param {number} [volume=0.3] - Ganancia pico de la nota (0.0 a 1.0).
 * @param {number} [attackMs=15] - Tiempo de ataque en ms para eliminar transitorios indeseados.
 * @param {number} [releaseMs=60] - Tiempo de caída / desvanecimiento en ms.
 * @returns {Promise<void>} Promesa que resuelve cuando finaliza el tono.
 */
export const playTone = async (
    frequency,
    durationMs = 300,
    waveform = 'sine',
    volume = 0.3,
    attackMs = 15,
    releaseMs = 60
) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
    }
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const durSec = Math.max(durationMs, 50) / 1000;
    const attackSec = Math.min(attackMs / 1000, durSec * 0.3);
    const releaseSec = Math.min(releaseMs / 1000, durSec * 0.4);
    const sustainSec = Math.max(durSec - attackSec - releaseSec, 0);

    // Oscilador principal
    const osc = ctx.createOscillator();
    osc.type = waveform;
    osc.frequency.setValueAtTime(frequency, now);

    // Oscilador secundario armónico sutil (triangle de relleno a -12dB)
    const harmonicOsc = ctx.createOscillator();
    harmonicOsc.type = 'triangle';
    harmonicOsc.frequency.setValueAtTime(frequency, now);

    // Envolvente de Ganancia ADSR
    const gainNode = ctx.createGain();
    const peakVolume = Math.max(Math.min(volume, 1.0), 0.01);
    const sustainVolume = peakVolume * 0.85;

    gainNode.gain.setValueAtTime(0.0001, now);
    // Ataque suave: 0 -> peak
    gainNode.gain.exponentialRampToValueAtTime(peakVolume, now + attackSec);
    // Decay a sustain
    if (sustainSec > 0) {
        gainNode.gain.linearRampToValueAtTime(sustainVolume, now + attackSec + (sustainSec * 0.2));
        gainNode.gain.setValueAtTime(sustainVolume, now + attackSec + sustainSec);
    }
    // Release suave: sustain -> 0.0001
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durSec);

    // Ganancia balanceada para el oscilador armónico secundario
    const harmonicGain = ctx.createGain();
    harmonicGain.gain.setValueAtTime(0.25, now);

    osc.connect(gainNode);
    harmonicOsc.connect(harmonicGain);
    harmonicGain.connect(gainNode);
    gainNode.connect(masterGain || ctx.destination);

    try {
        osc.start(now);
        harmonicOsc.start(now);

        const stopTime = now + durSec + 0.02;
        osc.stop(stopTime);
        harmonicOsc.stop(stopTime);

        // Desconectar nodos al finalizar para liberar memoria de audio
        setTimeout(() => {
            try {
                osc.disconnect();
                harmonicOsc.disconnect();
                gainNode.disconnect();
                harmonicGain.disconnect();
            } catch (e) {}
        }, durationMs + 100);
    } catch (e) {
        // En caso de que el AudioContext haya sido interrumpido
    }
};

/**
 * Reproduce el tono armónico correspondiente a uno de los 6 pads de Secuencia (0 a 5).
 * 
 * @param {number} padIndex - Índice del pad (0=440Hz, 1=554Hz, 2=659Hz, 3=784Hz, 4=880Hz, 5=988Hz).
 * @param {number} [durationMs=350] - Duración de reproducción en milisegundos.
 * @param {number} [volume=0.35] - Volumen del pad (0.0 a 1.0).
 * @returns {Promise<void>}
 */
export const playPadTone = async (padIndex, durationMs = 350, volume = 0.35) => {
    const safeIndex = Math.abs(Number(padIndex) || 0) % PAD_FREQUENCIES.length;
    const freq = PAD_FREQUENCIES[safeIndex];
    return await playTone(freq, durationMs, 'sine', volume, 15, 70);
};

/**
 * Reproduce una fanfarria armónica arpegiada de victoria cuando el jugador completa la secuencia correctamente.
 * Arpegio ascendente brillante: A4 (440Hz) -> C#5 (554Hz) -> E5 (659Hz) -> A5 (880Hz) -> B5 (988Hz) con acorde final sostenido.
 * 
 * @param {number} [volume=0.4] - Volumen de la fanfarria.
 * @returns {Promise<void>}
 */
export const playSuccessChord = async (volume = 0.4) => {
    const notes = [
        { freq: 440.00, delay: 0, dur: 180 },
        { freq: 554.37, delay: 100, dur: 180 },
        { freq: 659.25, delay: 200, dur: 180 },
        { freq: 880.00, delay: 300, dur: 350 },
        { freq: 987.77, delay: 420, dur: 700 }
    ];

    for (const note of notes) {
        setTimeout(() => {
            playTone(note.freq, note.dur, 'triangle', volume, 10, 100);
        }, note.delay);
    }
};

/** Alias de victoria */
export const playVictoryFanfare = playSuccessChord;

/**
 * Reproduce un tono de error característico (doble tono disonante grave con modulación descendente de pitch)
 * cuando el jugador se equivoca de botón o expira el tiempo.
 * 
 * @param {number} [volume=0.45] - Volumen del sonido de error.
 * @returns {Promise<void>}
 */
export const playErrorTone = async (volume = 0.45) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
    }
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const durSec = 0.55;

    // Oscilador 1: Onda diente de sierra grave con caída de tono (160Hz -> 80Hz)
    const osc1 = ctx.createOscillator();
    osc1.type = 'sawtooth';
    osc1.frequency.setValueAtTime(160, now);
    osc1.frequency.exponentialRampToValueAtTime(75, now + durSec);

    // Oscilador 2: Disonancia en tritono (115Hz -> 60Hz)
    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(115, now);
    osc2.frequency.exponentialRampToValueAtTime(60, now + durSec);

    // Filtro pasa-bajas para un sonido contundente y no estridente
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(600, now);
    filter.frequency.linearRampToValueAtTime(200, now + durSec);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0.001, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durSec);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(masterGain || ctx.destination);

    try {
        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + durSec + 0.02);
        osc2.stop(now + durSec + 0.02);

        setTimeout(() => {
            try {
                osc1.disconnect();
                osc2.disconnect();
                filter.disconnect();
                gainNode.disconnect();
            } catch (e) {}
        }, 650);
    } catch (e) {}
};

/**
 * Reproduce un bip sincronizado para la cuenta regresiva antes de reproducir la secuencia.
 * 
 * @param {boolean} [isGo=false] - Si es true, reproduce el tono agudo de inicio (1000Hz); si es false, un tick (600Hz).
 * @param {number} [volume=0.3] - Volumen del bip.
 * @returns {Promise<void>}
 */
export const playCountdownBeep = async (isGo = false, volume = 0.3) => {
    const freq = isGo ? 1046.50 : 587.33; // C6 (1046.5Hz) vs D5 (587.33Hz)
    const duration = isGo ? 300 : 150;
    return await playTone(freq, duration, 'sine', volume, 8, 40);
};

/**
 * Detiene o silencia de inmediato el contexto de audio global.
 */
export const stopAllSounds = () => {
    if (audioCtx && audioCtx.state === 'running') {
        try {
            masterGain?.gain.setValueAtTime(0, audioCtx.currentTime);
            // Restaurar volumen maestro tras 50ms
            setTimeout(() => {
                if (audioCtx && masterGain) {
                    masterGain.gain.setValueAtTime(0.7, audioCtx.currentTime);
                }
            }, 50);
        } catch (e) {}
    }
};

export default {
    PAD_FREQUENCIES,
    isAudioSupported,
    getAudioContext,
    unlockAudioContext,
    initAudioSynth,
    playTone,
    playPadTone,
    playSuccessChord,
    playVictoryFanfare,
    playErrorTone,
    playCountdownBeep,
    stopAllSounds
};
